<script setup lang="ts">
import { mdiThemeLightDark, mdiWeatherNight, mdiWhiteBalanceSunny } from '@mdi/js'
import { computed, onMounted } from 'vue'
import { useTheme } from 'vuetify'

const STORAGE_KEY = 'ls:theme'
const ORDER = ['system', 'light', 'dark'] as const
type Mode = (typeof ORDER)[number]

const theme = useTheme()

const mode = computed<Mode>(() => {
  if (theme.isSystem.value) return 'system'
  return theme.name.value === 'dark' ? 'dark' : 'light'
})

const icon = computed(() =>
  mode.value === 'system'
    ? mdiThemeLightDark
    : mode.value === 'dark'
      ? mdiWeatherNight
      : mdiWhiteBalanceSunny,
)
const label = computed(() => `Theme: ${mode.value} (click to change)`)

function remember(value: Mode) {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Storage may be unavailable (private mode); the choice then lasts for this visit only.
  }
}

async function next() {
  const index = ORDER.indexOf(mode.value)
  const value = ORDER[(index + 1) % ORDER.length] ?? 'system'
  await theme.change(value)
  remember(value)
}

onMounted(() => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Mode | null
    if (saved && ORDER.includes(saved) && saved !== mode.value) void theme.change(saved)
  } catch {
    // Ignore unavailable storage.
  }
})
</script>

<template>
  <v-btn :icon="icon" variant="text" :aria-label="label" :title="label" @click="next" />
</template>
